import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountReconciliationResult,
  CreateAccountReconciliationRequest,
  CreateAccountRequest,
  CreateBucketRequest,
  UpdateAccountRequest,
  UpdateBucketRequest,
} from '@financial/shared';
import {
  createAccountReconciliation,
  createAccount,
  createBucket,
  deleteAccount,
  deleteBucket,
  getNetWorth,
  listAccountReconciliations,
  listAccounts,
  updateAccount,
  updateBucket,
  undoAccountReconciliation,
} from './api';
import { currentCurrency } from '../../lib/format';
import { notify } from '../../store/notifications';

const ACCOUNTS_KEY = ['accounts'];
const NET_WORTH_KEY = ['net-worth'];
const RECONCILIATIONS_KEY = ['account-reconciliations'];

export function useAccounts() {
  return useQuery({ queryKey: ACCOUNTS_KEY, queryFn: listAccounts });
}

export function useNetWorth() {
  const currency = currentCurrency();
  return useQuery({ queryKey: [...NET_WORTH_KEY, currency], queryFn: () => getNetWorth(currency) });
}

export function useAccountReconciliations(accountId: string) {
  return useQuery({
    queryKey: [...RECONCILIATIONS_KEY, accountId],
    queryFn: () => listAccountReconciliations(accountId),
  });
}

function useInvalidateAccounts() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    void qc.invalidateQueries({ queryKey: NET_WORTH_KEY });
    void qc.invalidateQueries({ queryKey: RECONCILIATIONS_KEY });
    // Un saldo/apartado puede respaldar una meta y también cambia la liquidez
    // y las recomendaciones. Refrescamos estas vistas para no mostrar una
    // meta o un panorama desactualizado después de conciliar.
    void qc.invalidateQueries({ queryKey: ['savings-goals'] });
    void qc.invalidateQueries({ queryKey: ['budget'] });
    void qc.invalidateQueries({ queryKey: ['insights'] });
  };
}

export function useCreateAccountReconciliation() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (vars: { accountId: string; body: CreateAccountReconciliationRequest }) =>
      createAccountReconciliation(vars.accountId, vars.body),
    onSuccess: (result, variables) => {
      invalidate();
      notify.success('Saldo actualizado sin crear ingreso ni gasto', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await undoAccountReconciliation(variables.accountId, result.reconciliation.id);
            invalidate();
            notify.info('Conciliación deshecha');
          },
        },
      });
    },
    // Un 409 indica que otra pestaña o un movimiento cambió el saldo. Refrescar
    // la cuenta recalcula la diferencia y el siguiente intento usa la nueva
    // fotografía en `expectedBalance`.
    onError: invalidate,
  });
}

export function useUndoAccountReconciliation() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (vars: { accountId: string; reconciliationId: string }) =>
      undoAccountReconciliation(vars.accountId, vars.reconciliationId),
    onSuccess: (_result: AccountReconciliationResult) => {
      invalidate();
      notify.info('Conciliación deshecha');
    },
  });
}

export function useCreateAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (body: CreateAccountRequest) => createAccount(body),
    onSuccess: (account) => {
      invalidate();
      notify.success('Cuenta creada', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await deleteAccount(account.id);
            invalidate();
            notify.info('Cuenta deshecha');
          },
        },
      });
    },
  });
}

export function useDeleteAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({ mutationFn: (id: string) => deleteAccount(id), onSuccess: invalidate });
}

export function useUpdateAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (vars: { id: string; body: UpdateAccountRequest }) =>
      updateAccount(vars.id, vars.body),
    onSuccess: () => {
      invalidate();
      notify.success('Cuenta actualizada');
    },
  });
}

export function useCreateBucket(accountId: string) {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (body: CreateBucketRequest) => createBucket(accountId, body),
    onSuccess: (bucket, variables) => {
      invalidate();
      if (variables.addToAccountBalance) {
        notify.success('Apartado creado y saldo total actualizado');
        return;
      }
      notify.success('Apartado creado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await deleteBucket(accountId, bucket.id);
            invalidate();
            notify.info('Apartado deshecho');
          },
        },
      });
    },
  });
}

export function useUpdateBucket(accountId: string) {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (vars: { bucketId: string; body: UpdateBucketRequest }) =>
      updateBucket(accountId, vars.bucketId, vars.body),
    onSuccess: () => {
      invalidate();
      notify.success('Apartado actualizado');
    },
  });
}

export function useDeleteBucket(accountId: string) {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (bucketId: string) => deleteBucket(accountId, bucketId),
    onSuccess: invalidate,
  });
}
